import React, { useState } from 'react';
import { Trash2Icon, XIcon } from 'lucide-react';
import { useInstrumentConfig } from '../../contexts/AssessmentContext';
import { FlagRecord, ProposedActionType, SignOffAnswer } from '../../types/assessment';
interface Props {
  rowId: string;
  rowLabel: string;
  kind: 'flag' | 'correction';
  existing?: FlagRecord;
  onSave: (record: FlagRecord) => void;
  onDelete: () => void;
  onClose: () => void;
}
export function FlagDrawer({
  rowId,
  rowLabel,
  kind,
  existing,
  onSave,
  onDelete,
  onClose
}: Props) {
  const config = useInstrumentConfig();
  const {
    documentOptions,
    drawer
  } = config;
  const ACTIONS = drawer.actions;
  const SIGN_OFFS: {
    id: SignOffAnswer;
    label: string;
  }[] = drawer.signOffs;
  const defaultAction: ProposedActionType = kind === 'correction' ? drawer.entryActions[0] || drawer.escalateActions[0] || 'note' : 'note';
  const [what, setWhat] = useState(existing?.what || '');
  const [sources, setSources] = useState<string[]>(existing?.sources || []);
  const [actionType, setActionType] = useState<ProposedActionType>(existing?.actionType || defaultAction);
  const [debit, setDebit] = useState(existing?.debit || '');
  const [credit, setCredit] = useState(existing?.credit || '');
  const [amount, setAmount] = useState(existing?.amount || '');
  const [narrative, setNarrative] = useState(existing?.narrative || '');
  const [escalateTo, setEscalateTo] = useState(existing?.escalateTo || '');
  const [escalateWhy, setEscalateWhy] = useState(existing?.escalateWhy || '');
  const [signOff, setSignOff] = useState<SignOffAnswer | null>(existing?.signOff || null);
  const needsEntry = drawer.entryActions.includes(actionType);
  const needsEscalation = drawer.escalateActions.includes(actionType);
  const canSave = what.trim().length > 0 && signOff !== null;
  const toggleSource = (id: string) => setSources(prev => prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]);
  const save = () => {
    if (!canSave || !signOff) return;
    onSave({
      rowId,
      // Where the instrument has no bookkeeping-entry actions, the kind is simply
      // whichever action the row invoked.
      kind: drawer.entryActions.length ? needsEntry ? 'correction' : 'flag' : kind,
      what: what.trim(),
      sources,
      actionType,
      debit: debit.trim(),
      credit: credit.trim(),
      amount: amount.trim(),
      narrative: narrative.trim(),
      escalateTo: escalateTo.trim(),
      escalateWhy: escalateWhy.trim(),
      signOff,
      at: Date.now()
    });
    onClose();
  };
  return <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true" aria-label={`Entry for ${rowId}`} data-id="element-251" data-mp="3u2qa2qtibqfi">
      <button type="button" aria-label="Close" onClick={onClose} className="flex-1 bg-slate-900/30" data-id="element-252" data-mp="2vm6k5wkmmbu" />
      <aside className="flex h-full w-[540px] flex-col border-l border-slate-200 bg-white shadow-2xl" data-id="element-253" data-mp="2im8bol6cndq8">
        <header className="flex items-start justify-between border-b border-slate-200 px-6 py-4" data-id="element-254" data-mp="2nnxn41xhhteu">
          <div data-id="element-255" data-mp="15n6kslen1jig">
            <p className="font-mono-num text-xs text-slate-500" data-id="element-256" data-mp="15qudjic9e1w4">{rowId}</p>
            <h2 className="mt-0.5 text-[15px] font-semibold text-slate-900" data-id="element-257" data-mp="10poj9g2ya6kh">{rowLabel}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Close drawer" className="rounded-md p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" data-id="element-258" data-mp="1eowi27v7bco5">
            <XIcon className="h-4 w-4" data-id="element-259" data-mp="hzou6t740rh2" />
          </button>
        </header>

        <div className="flex-1 space-y-6 overflow-y-auto px-6 py-5" data-id="element-260" data-mp="hxlybsja044f">
          <div data-id="element-261" data-mp="vsejb6hxqcie">
            <label htmlFor="what" className="block text-sm font-medium text-slate-900" data-id="element-262" data-mp="vsy4qx6snj16">
              {drawer.whatLabel} <span className="font-normal text-slate-400" data-id="element-263" data-mp="qyaasit0twf7">required</span>
            </label>
            <textarea id="what" rows={3} value={what} onChange={e => setWhat(e.target.value)} placeholder="One sentence." className="mt-2 w-full rounded-lg border border-slate-300 p-3 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200" data-id="element-264" data-mp="38g3shvdv22v" />
          </div>

          <fieldset data-id="element-265" data-mp="qmsy9lcevqle">
            <legend className="text-sm font-medium text-slate-900" data-id="element-266" data-mp="qncij8pjar2l">{drawer.sourcesLabel}</legend>
            <div className="mt-2 space-y-1.5" data-id="element-267" data-mp="vmxd76xo8d9i">
              {documentOptions.map(opt => <label key={opt.id} className="flex cursor-pointer items-start gap-2.5 rounded-md border border-slate-200 px-3 py-2 text-[13px] text-slate-700 transition-colors hover:border-slate-300" data-id="element-268" data-mp="zmzgfkj21eqw">
                  <input type="checkbox" checked={sources.includes(opt.id)} onChange={() => toggleSource(opt.id)} className="mt-0.5 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-400" data-id="element-269" data-mp="19jko6dmgl3yf" />
                  <span data-id="element-270" data-mp="33vlt33cp4qc4">{opt.label}</span>
                </label>)}
            </div>
          </fieldset>

          <fieldset data-id="element-271" data-mp="2u3pr3h7hog2m">
            <legend className="text-sm font-medium text-slate-900" data-id="element-272" data-mp="2u36ca9d4y7st">Proposed action</legend>
            <div className="mt-2 grid grid-cols-2 gap-2" data-id="element-273" data-mp="2p5eg335gjzit">
              {ACTIONS.map(a => <button key={a.id} type="button" onClick={() => setActionType(a.id)} className={`rounded-lg border px-3 py-2.5 text-left transition-colors ${actionType === a.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`} data-id="element-274" data-mp="3mt6ge5ztbgum">
                  <span className="block text-[13px] font-medium" data-id="element-275" data-mp="21cxq86fhb38y">{a.label}</span>
                  <span className={`block text-[11px] ${actionType === a.id ? 'text-slate-300' : 'text-slate-500'}`} data-id="element-276" data-mp="1wew8botsh9z2">
                    {a.hint}
                  </span>
                </button>)}
            </div>

            {needsEntry && <div className="mt-3 grid grid-cols-2 gap-3 rounded-lg bg-slate-50 p-3" data-id="element-277" data-mp="jh5opyuqf5n5">
                <div data-id="element-278" data-mp="4t15mtrros3u">
                  <label htmlFor="dr" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-279" data-mp="4sixayrtynfk">
                    Debit account
                  </label>
                  <input id="dr" value={debit} onChange={e => setDebit(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-280" data-mp="3ry0jer1dljq5" />
                </div>
                <div data-id="element-281" data-mp="3mxw0kuczwcwr">
                  <label htmlFor="cr" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-282" data-mp="3n0i5snceygbt">
                    Credit account
                  </label>
                  <input id="cr" value={credit} onChange={e => setCredit(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-283" data-mp="2zclfeohz64ld" />
                </div>
                <div data-id="element-284" data-mp="3ia4o1r0itl79">
                  <label htmlFor="amt" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-285" data-mp="3iao5l6m9e7kr">
                    {drawer.amountLabel}
                  </label>
                  <input id="amt" value={amount} onChange={e => setAmount(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 font-mono-num text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-286" data-mp="3r1oiw221fawy" />
                </div>
                <div className="col-span-2" data-id="element-287" data-mp="2lq4kstrh43qa">
                  <label htmlFor="narr" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-288" data-mp="2qmlgc9105kde">
                    Narrative
                  </label>
                  <textarea id="narr" rows={2} value={narrative} onChange={e => setNarrative(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-289" data-mp="30l101qxp9rcv" />
                </div>
              </div>}

            {needsEscalation && <div className="mt-3 space-y-3 rounded-lg bg-slate-50 p-3" data-id="element-290" data-mp="2hnzsbycrhd58">
                <div data-id="element-291" data-mp="1k7ubxvco7hem">
                  <label htmlFor="to" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-292" data-mp="1k6ja412scjq5">
                    {drawer.escalateToLabel}
                  </label>
                  <input id="to" value={escalateTo} onChange={e => setEscalateTo(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-293" data-mp="69q3ela232i2" />
                </div>
                <div data-id="element-294" data-mp="3jhjd89pyc8mq">
                  <label htmlFor="why" className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500" data-id="element-295" data-mp="3ji2rngge8ew0">
                    Why
                  </label>
                  <textarea id="why" rows={2} value={escalateWhy} onChange={e => setEscalateWhy(e.target.value)} className="mt-1 w-full rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-slate-500 focus:outline-none" data-id="element-296" data-mp="3ap928q78fz4f" />
                </div>
              </div>}
          </fieldset>

          <fieldset data-id="element-297" data-mp="383l31l60edsg">
            <legend className="text-sm font-medium text-slate-900" data-id="element-298" data-mp="3831yd65ddlwd">{drawer.signOffLegend}</legend>
            <div className="mt-2 flex gap-2" data-id="element-299" data-mp="3350e4hw7jgjb">
              {SIGN_OFFS.map(s => <button key={s.id} type="button" onClick={() => setSignOff(s.id)} className={`flex-1 rounded-lg border px-3 py-2 text-[13px] font-medium transition-colors ${signOff === s.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'}`} data-id="element-300" data-mp="3gvn1jdwipk03">
                  {s.label}
                </button>)}
            </div>
          </fieldset>
        </div>

        <footer className="flex items-center justify-between gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4" data-id="element-301" data-mp="dewq2ji62er0">
          {existing ? <button type="button" onClick={() => {
          onDelete();
          onClose();
        }} className="inline-flex items-center gap-1.5 text-[13px] font-medium text-slate-500 transition-colors hover:text-red-600" data-id="element-302" data-mp="8jhclso2mkv4">
              <Trash2Icon className="h-3.5 w-3.5" data-id="element-303" data-mp="72aoct2im6n8" />
              Remove entry
            </button> : <span data-id="element-304" data-mp="zovexrb62epq" />}
          <div className="flex items-center gap-2" data-id="element-305" data-mp="zpoeo8bpk3yd">
            <button type="button" onClick={onClose} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-[13px] font-medium text-slate-700 transition-colors hover:border-slate-400" data-id="element-306" data-mp="zqziu992fdq0">
              Cancel
            </button>
            <button type="button" onClick={save} disabled={!canSave} className="rounded-md bg-slate-900 px-5 py-2 text-[13px] font-semibold text-white transition-colors hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-300" data-id="element-307" data-mp="uvk5dieyzju4">
              Save
            </button>
          </div>
        </footer>
      </aside>
    </div>;
}